import React, { useState, useRef, useEffect, useCallback } from 'react';
import './M3Menu.css';

/**
 * M3 Menu — standard menu anchored to a trigger.
 *
 * @param {ReactNode} trigger — element that opens the menu
 * @param {Array<{key, label, icon?, leadingIcon?, onClick?}>} items
 * @param {'top'|'bottom'|'start'|'end'} [anchor='bottom']
 * @param {ReactNode} [header] — optional header slot
 * @param {ReactNode} [footer] — optional footer slot
 */
export default function M3Menu({
  trigger,
  items = [],
  anchor = 'bottom',
  header,
  footer,
  className = ''
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div className={`m3-menu-container ${className}`}>
      <div ref={triggerRef} onClick={toggle} className="m3-menu-trigger">
        {trigger}
      </div>
      {open && (
        <div
          ref={menuRef}
          className={`m3-menu m3-menu-${anchor}`}
          role="menu"
          aria-orientation="vertical"
        >
          {header && (
            <div className="m3-menu-header" role="presentation">
              {header}
            </div>
          )}
          {items.map((item) => (
            <button
              key={item.key}
              className="m3-menu-item"
              role="menuitem"
              onClick={() => {
                item.onClick?.();
                setOpen(false);
              }}
            >
              {item.leadingIcon && <span className="m3-menu-icon">{item.leadingIcon}</span>}
              <span className="m3-menu-label">{item.label}</span>
              {item.trailingIcon && (
                <span className="m3-menu-icon m3-menu-icon-trailing">{item.trailingIcon}</span>
              )}
            </button>
          ))}
          {footer && (
            <div className="m3-menu-footer" role="presentation">
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Simple icon-button trigger that opens a menu.
 */
export function MenuTrigger({ children, items, anchor, header, footer, className }) {
  return (
    <M3Menu
      trigger={<span className="m3-menu-trigger-btn">{children}</span>}
      items={items}
      anchor={anchor}
      header={header}
      footer={footer}
      className={className}
    />
  );
}
